import {
  ITabelFieldViewProps,
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  GET_ALL_PAYMENT_TRANSACTION,
  GLOBAL_DROP_DOWN,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
  PAYMENT_ENTITY_STATUS_LABEL,
  PAYMENT_ENTITY_TYPE_LABEL,
} from "../../utils/AppConstants";
import {
  PAYMENT_ENTITY_TYPE,
  PAYMENT_STATUS,
} from "../../utils/AppEnumerations";
import {
  handleError,
  sweetAlertError,
  toastError,
} from "../../utils/AppFunctions";

export interface IPaymentTransaction {
  firm_name: string;
  entity_type: string;
  payment_for: string;
  payment_transaction_status: string;
  transaction_amount: number;
  transaction_currency: string;
  invoice_number: number;
  payment_captured_date: Date;
  payment_method: string;
  payment_failed_reason: string;
  pg_order_id: string;
}
export interface IPaymentTransactionSearch {
  firmId: string | null;
  entityType: string;
  fromDate: string;
  toDate: string;
  pgOrder: string;
  paymentTransactionStatus: string;
  invoiceNumber: string;
  entityName: string;
}
export const PAYMENT_TRANSACTION_SEARCH_INITIAL_VALUE = {
  firmId: null,
  entityType: "",
  fromDate: "",
  toDate: "",
  pgOrder: "",
  paymentTransactionStatus: "",
  invoiceNumber: "",
  entityName: "",
};
export function getPaymentTransaction<T>(
  actionComponent: (item: T) => JSX.Element,
  t: TUseTranslationTfn
): ITableFields<T>[] {
  return [
    {
      name: t("firm_name"),
      key: "firm_name",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },
    {
      name: t("entity_type"),
      key: "entity_type",
      sort: true,
      type: TABLE_FIELD_TYPE.format,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
      format: ({ field, data }: ITabelFieldViewProps<T>) => {
        if (Object.values(PAYMENT_ENTITY_TYPE).includes(data["entity_type"])) {
          return PAYMENT_ENTITY_TYPE_LABEL[
            data["entity_type"] as keyof typeof PAYMENT_ENTITY_TYPE_LABEL
          ];
        }
        return "-";
      },
    },
    {
      name: t("package"),
      key: "payment_for",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-1",
      assistiveText: t("subscription_package_name"),
    },
    {
      name: t("transaction_amount_title"),
      key: "transaction_amount",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.right,
      flex: "col-span-2",
      assistiveText: t("transaction_amount"),
    },
    {
      name: t("txn_date"),
      key: "payment_captured_date",
      sort: true,
      type: TABLE_FIELD_TYPE.date,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
      assistiveText: t("payment_captured_date"),
    },
    {
      name: t("invoice_id"),
      key: "invoice_number",
      sort: false,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
      assistiveText: t("invoice_number"),
    },
    {
      name: t("order_id"),
      key: "pg_order_id",
      sort: false,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },

    {
      name: t("payment_method"),
      key: "payment_method",
      sort: true,
      type: TABLE_FIELD_TYPE.format,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
      format: ({ field, data }: ITabelFieldViewProps<T>) =>
        data["payment_method"] ? data["payment_method"] : "-",
    },
    {
      name: t("status"),
      key: "payment_transaction_status",
      sort: true,
      type: TABLE_FIELD_TYPE.status,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
      assistiveText: t("payment_transaction_status"),
      statusColorClassPair: {
        [PAYMENT_STATUS.Initiated]: "bg-warning-muted text-warning",
        [PAYMENT_STATUS.Failed]: "bg-danger-muted text-danger",
        [PAYMENT_STATUS.Succeeded]: "bg-success-muted text-success",
      },
      statusLabelPair: {
        [PAYMENT_STATUS.Initiated]:
          PAYMENT_ENTITY_STATUS_LABEL[PAYMENT_STATUS.Initiated],
        [PAYMENT_STATUS.Failed]:
          PAYMENT_ENTITY_STATUS_LABEL[PAYMENT_STATUS.Failed],
        [PAYMENT_STATUS.Succeeded]:
          PAYMENT_ENTITY_STATUS_LABEL[PAYMENT_STATUS.Succeeded],
      },
    },

    {
      name: t("action"),
      key: "action",
      sort: false,
      type: TABLE_FIELD_TYPE.custom,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-1",
      component: actionComponent,
    },
  ];
}

export const getAllPaymentTransaction = async (
  setPaymentTransactionList: TReactSetState<IPaymentTransaction[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  advanceSearch: IPaymentTransactionSearch
) => {
  try {
    const paymentTransaction = await GET_ALL_PAYMENT_TRANSACTION(
      pagination,
      search,
      advanceSearch
    );
    if (
      paymentTransaction &&
      paymentTransaction.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      setPaymentTransactionList(paymentTransaction.data.items);
      setPagination(paymentTransaction.data.pagination);
    } else {
      setPaymentTransactionList([]);
      sweetAlertError({
        message: paymentTransaction.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (error: any) {
    handleError(error, "getAllPaymentTransaction");
  }
};

export const getFirmNameDdl = async (
  setFirmName: TReactSetState<IOption<string>[]>
) => {
  try {
    const payload = {
      entity: "firms",
      data_value: "id",
      display_value: "firm_name",
      filters: [
        {
          field: "is_deleted",
          value: "1|0",
          condition: "eq",
        },
      ],
      additional_field: ["is_deleted"],
    };
    const firmNameDDLList = await GLOBAL_DROP_DOWN(payload);
    if (
      firmNameDDLList &&
      firmNameDDLList.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      if (firmNameDDLList.data) {
        setFirmName(firmNameDDLList.data);
      } else {
        setFirmName([]);
      }
    } else {
      toastError(firmNameDDLList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    }
  } catch (e: any) {
    toastError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "getFirmNameDdl");
  }
};
